import React, { useState, useEffect, useRef } from 'react';
import { Clock, Info } from 'lucide-react';

function pad(n) { return String(n).padStart(2, '0'); }

function getNextEvent(events) {
  if (!events || events.length === 0) return null;
  const now = new Date();
  const future = events
    .filter(e => {
      const eventDate = new Date(e.date + 'T' + (e.time || '23:59'));
      return eventDate >= now;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  return future[0] || null;
}

function useCountdown(targetDate) {
  const [timeLeft, setTimeLeft] = useState(null);
  useEffect(() => {
    if (!targetDate) return;
    const target = new Date(targetDate);
    const calc = () => {
      const diff = target - new Date();
      if (diff <= 0) return setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      setTimeLeft({
        days:    Math.floor(diff / 86400000),
        hours:   Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000)  / 60000),
        seconds: Math.floor((diff % 60000)    / 1000),
      });
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [targetDate]);
  return timeLeft;
}

function formatEventDate(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      weekday: 'long', day: 'numeric', month: 'long',
    });
  } catch { return dateStr; }
}

// Animated background canvas: slow spotlight beams like a dance floor
function SpotlightCanvas() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let frame = 0;
    let animId;
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener('resize', resize);

    const spots = [
      { x: 0.15, y: 0, r: 0.6, color: '201,168,76',  speed: 0.004, phase: 0 },
      { x: 0.5,  y: 0, r: 0.5, color: '79,195,212',  speed: 0.005, phase: 1.5 },
      { x: 0.85, y: 0, r: 0.6, color: '201,168,76',  speed: 0.003, phase: 3 },
      { x: 0.3,  y: 0, r: 0.4, color: '120,80,200',  speed: 0.006, phase: 0.8 },
      { x: 0.7,  y: 0, r: 0.45,color: '79,195,212',  speed: 0.004, phase: 2.2 },
    ];

    const draw = () => {
      frame++;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const t = frame * 0.012;

      spots.forEach(s => {
        const cx = canvas.width  * (s.x + Math.sin(t * s.speed * 80 + s.phase) * 0.12);
        const cy = canvas.height * (0.5 + Math.cos(t * s.speed * 60 + s.phase) * 0.35);
        const rx = canvas.width  * s.r;
        const ry = canvas.height * s.r * 0.6;

        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, rx);
        const alpha = 0.06 + Math.sin(t + s.phase) * 0.025;
        grad.addColorStop(0,   `rgba(${s.color}, ${alpha})`);
        grad.addColorStop(0.4, `rgba(${s.color}, ${alpha * 0.4})`);
        grad.addColorStop(1,   'transparent');

        ctx.save();
        ctx.scale(1, ry / rx);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(cx, cy * (rx / ry), rx, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });

      // subtle horizontal light streaks at bottom (dance floor reflection)
      for (let i = 0; i < 3; i++) {
        const y = canvas.height * (0.75 + i * 0.08 + Math.sin(t * 0.3 + i) * 0.03);
        const alpha = 0.04 + Math.sin(t * 0.5 + i * 1.2) * 0.02;
        const grad = ctx.createLinearGradient(0, y, canvas.width, y);
        grad.addColorStop(0,   'transparent');
        grad.addColorStop(0.3, `rgba(201,168,76,${alpha})`);
        grad.addColorStop(0.7, `rgba(79,195,212,${alpha})`);
        grad.addColorStop(1,   'transparent');
        ctx.fillStyle = grad;
        ctx.fillRect(0, y - 1, canvas.width, 2);
      }

      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);
  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ mixBlendMode: 'screen' }}
    />
  );
}

export default function Countdown({ events, onEventDetail }) {
  const nextEvent = getNextEvent(events);
  const timeLeft  = useCountdown(nextEvent?.date);

  if (!nextEvent || !timeLeft) return null;

  const units = [
    { label: 'Giorni',  value: timeLeft.days },
    { label: 'Ore',     value: timeLeft.hours },
    { label: 'Minuti',  value: timeLeft.minutes },
    { label: 'Secondi', value: timeLeft.seconds },
  ];

  return (
    <section
      data-testid="countdown-section"
      className="relative py-16 overflow-hidden"
      style={{ background: '#060610' }}
    >
      {/* Animated spotlight background */}
      <SpotlightCanvas />

      {/* Top & bottom neon lines */}
      <div className="absolute top-0 left-0 right-0 neon-line" style={{ opacity: 0.6 }} />
      <div className="absolute bottom-0 left-0 right-0 neon-line" style={{ opacity: 0.6 }} />

      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row items-center gap-10 md:gap-14">

          {/* Locandina */}
          <div className="shrink-0 w-full md:w-60">
            <div
              className="rounded-2xl overflow-hidden"
              style={{
                border: '1px solid rgba(201,168,76,0.4)',
                boxShadow: '0 0 40px rgba(201,168,76,0.2), 0 0 80px rgba(79,195,212,0.1)',
                height: '300px',
              }}
            >
              <img
                src={nextEvent.image_url || '/api/uploads/logo_fallback.png'}
                alt={nextEvent.title}
                className="w-full h-full"
                style={nextEvent.image_url ? { objectFit: 'cover' } : { objectFit: 'contain', padding: '2rem', background: '#0a0a0f' }}
                onError={e => { e.target.style.objectFit='contain'; e.target.style.padding='2rem'; e.target.style.background='#0a0a0f'; e.target.src='/api/uploads/logo_fallback.png'; }}
              />
            </div>
          </div>

          {/* Info + timer */}
          <div className="flex-1 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-2 mb-3">
              <Clock size={16} className="text-aqua" />
              <p className="section-eyebrow" style={{ marginBottom: 0 }}>Prossima serata</p>
            </div>

            <h3 className="font-playfair text-2xl md:text-4xl font-bold text-white mb-2 leading-tight">
              {nextEvent.title}
            </h3>

            {nextEvent.special_guest && (
              <div
                className="inline-flex items-center gap-2 my-2 px-4 py-1.5 rounded-full font-manrope font-700 text-sm"
                style={{
                  background: 'linear-gradient(90deg, rgba(201,168,76,0.15), rgba(201,168,76,0.25))',
                  border: '1px solid rgba(201,168,76,0.5)',
                  color: 'var(--color-gold)',
                  boxShadow: '0 0 20px rgba(201,168,76,0.2)',
                }}
              >
                ★ Special Guest: {nextEvent.special_guest}
              </div>
            )}

            <p className="text-gray-400 font-manrope text-base mb-8 capitalize">
              {formatEventDate(nextEvent.date)} &bull; ore {nextEvent.time}
            </p>

            {/* Countdown boxes */}
            <div className="flex justify-center md:justify-start gap-3 md:gap-5 mb-8">
              {units.map((u, i) => (
                <div key={u.label} className="flex flex-col items-center gap-1.5">
                  <div
                    style={{
                      width: '76px', height: '76px',
                      background: 'linear-gradient(135deg, rgba(201,168,76,0.08) 0%, rgba(13,27,42,0.9) 100%)',
                      border: '1px solid rgba(201,168,76,0.3)',
                      borderRadius: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)',
                      backdropFilter: 'blur(8px)',
                    }}
                  >
                    <span className="font-playfair text-3xl font-bold" style={{ color: 'var(--color-gold)' }}>
                      {pad(u.value)}
                    </span>
                  </div>
                  <span className="text-gray-500 font-manrope text-xs uppercase tracking-widest">
                    {u.label}
                  </span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap justify-center md:justify-start gap-3">
              <button
                onClick={() => document.getElementById('prenota')?.scrollIntoView({ behavior: 'smooth' })}
                className="btn-primary"
                data-testid="countdown-cta"
              >
                PRENOTA PER QUESTA SERATA
              </button>
              {onEventDetail && (
                <button
                  onClick={() => onEventDetail(nextEvent)}
                  className="btn-secondary flex items-center gap-2"
                  data-testid="countdown-info-btn"
                >
                  <Info size={18} /> INFO
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
